import { useLocation } from 'react-router-dom';

import { IconApi } from '@components/icons/api';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconCompany } from '@components/icons/company';
import { IconEmail } from '@components/icons/email';
import { IconPlan } from '@components/icons/icon-plan';
import { IconIntegration } from '@components/icons/integration';
import { IconIntegrations } from '@components/icons/integrations';
import { IconLock } from '@components/icons/lock';
import { IconPayment } from '@components/icons/payment';
import { IconPerson } from '@components/icons/person';
import { IconPortal } from '@components/icons/portal';
import { IconSSO } from '@components/icons/sso';
import { IconStatistics } from '@components/icons/statistics';
import { IconTeam } from '@components/icons/team';
import { Nav } from '@components/nav';
import { Link, NavGroupCollapse } from '@components/nav-group-collapse/nav-group-collapse';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { createSelectionStore } from '@stores/create-selection-store';

import { usePlan } from 'api/authentication/hooks';
import { usePermissions } from 'contexts/permissions-context';
import { useFeature } from 'ui/hooks';

import { routePaths } from '../../navigation';

type LocationState = {
  from: string;
};

type Group = {
  id: string;
  label: string;
  links: Link[];
};

function filterVisible<T>(items: (T & { visible?: boolean })[]): T[] {
  return items.filter((item) => item.visible !== false).map(({ visible, ...rest }) => rest as T);
}

const expandedStore = createSelectionStore({ persistKey: 'company-nav-state' });

const NAV_ICON_SIZE = 16;

export const SettingsNavbar = () => {
  const { t } = useScopedTranslate(locale);
  const location = useLocation();
  const { from } = (location.state as LocationState) || { from: '/' };
  const plan = usePlan();
  const { isEnabled: hasMCPServers } = useFeature('FEATURE_MCP_SERVERS');
  const { isEnabled: hasMarketplaceEnabled } = useFeature('FEATURE_MARKETPLACE');
  const {
    canManageCompany,
    canManageUsers,
    canManageEmailDomains,
    canManageSecurity,
    canManageIntegrations,
    canManageBilling,
    canManagePortals,
    canManageApiKeys,
  } = usePermissions();

  const organizationLinks = filterVisible<Link>([
    {
      visible: canManageCompany,
      id: 'organization-general',
      href: routePaths.settingsGeneral,
      icon: <IconCompany size={NAV_ICON_SIZE} />,
      label: t('general'),
    },
    {
      visible: canManageUsers,
      id: 'organization-users',
      href: routePaths.settingsUsers,
      icon: <IconTeam size={NAV_ICON_SIZE} />,
      label: t('members'),
    },
    {
      visible: canManageEmailDomains,
      id: 'organization-email-domain',
      href: routePaths.settingsEmailDomain,
      icon: <IconEmail size={NAV_ICON_SIZE} />,
      label: t('emailDomain'),
    },
    {
      visible: canManageSecurity,
      id: 'organization-security',
      href: routePaths.settingsOrganizationSecurity,
      icon: <IconSSO size={NAV_ICON_SIZE} />,
      label: t('security'),
    },
    {
      visible: canManageIntegrations && !hasMarketplaceEnabled,
      id: 'organization-integrations',
      href: routePaths.integrations,
      icon: <IconIntegration size={NAV_ICON_SIZE} />,
      label: t('integrations'),
    },
    {
      visible: canManageIntegrations && hasMCPServers,
      id: 'organization-mcp-servers',
      href: routePaths.mcpServers,
      icon: <IconIntegration size={NAV_ICON_SIZE} />,
      label: t('mcpServers'),
    },
  ]);

  const accountLinks = filterVisible<Link>([
    {
      id: 'account-settings',
      href: routePaths.settingsProfile,
      icon: <IconPerson size={NAV_ICON_SIZE} />,
      label: t('profile'),
    },
    {
      id: 'account-security',
      href: routePaths.settingsAccountSecurity,
      icon: <IconLock size={NAV_ICON_SIZE} />,
      label: t('security'),
    },
    {
      visible: canManageApiKeys,
      id: 'account-api',
      href: routePaths.settingsApi,
      icon: <IconApi size={NAV_ICON_SIZE} />,
      label: t('api'),
    },
  ]);

  const groups = filterVisible<Group>([
    {
      visible: organizationLinks.length > 0,
      id: 'organization',
      label: t('organization'),
      links: organizationLinks,
    },
    {
      visible: !!plan && canManageBilling,
      id: 'subscription',
      label: t('subscription'),
      links: [
        {
          id: 'subscription-billing',
          href: routePaths.subscriptionBilling,
          icon: <IconPayment size={NAV_ICON_SIZE} />,
          label: t('billing'),
        },
        {
          id: 'subscription-plans',
          href: routePaths.subscriptionPlans,
          icon: <IconPlan size={NAV_ICON_SIZE} />,
          label: t('plans'),
        },
      ],
    },
    {
      visible: canManagePortals,
      id: 'portal',
      label: t('portal'),
      links: [
        {
          id: 'portal-portals',
          href: routePaths.portals,
          icon: <IconPortal size={NAV_ICON_SIZE} />,
          label: t('portals'),
        },
        {
          id: 'portal-statistics',
          href: routePaths.portalStatistics,
          icon: <IconStatistics size={NAV_ICON_SIZE} />,
          label: t('statistics'),
        },
      ],
    },
    {
      visible: canManageIntegrations && hasMarketplaceEnabled,
      id: 'integrations',
      label: t('integrations'),
      links: [
        {
          id: 'integrations-marketplace',
          href: routePaths.appMarketplace,
          icon: <IconIntegrations size={NAV_ICON_SIZE} />,
          label: t('appMarketplace'),
        },
        {
          id: 'integrations-custom',
          href: routePaths.integrations,
          icon: <IconIntegration size={NAV_ICON_SIZE} />,
          label: t('customIntegrations'),
        },
      ],
    },
    { id: 'account', label: t('account'), links: accountLinks },
  ]);

  return (
    <Nav
      stateStorageKey="company-nav-state"
      variant="static"
      fixedHeader={
        <Nav.Header
          logo={
            <Nav.Link
              href={from}
              label={<Text font="bold14" caps text={t('back')} color="white100" />}
              iconLeft={<IconArrowLeft size={NAV_ICON_SIZE} />}
            />
          }
        />
      }
    >
      {groups.map((group) => (
        <NavGroupCollapse
          key={group.id}
          id={group.id}
          label={group.label}
          links={group.links}
          toggleGroup={expandedStore.toggle}
          expandedSelectionStore={expandedStore}
        />
      ))}
      <Nav.Spacer />
    </Nav>
  );
};

const locale = {
  en: {
    organization: 'Organization',
    general: 'General',
    members: 'Members',
    emailDomain: 'Email domain',
    account: 'Account',
    profile: 'Profile',
    security: 'Security',
    adminSettings: 'Admin settings',
    back: 'Back',
    team: 'Team',
    templates: 'Templates',
    api: 'API keys',
    integrations: 'Integrations',
    lists: 'Lists',
    documents: 'Documents',
    workflowHub: 'Workflow hub',
    journeyFieldTemplates: 'Journey fields',
    portal: 'Portal',
    subscription: 'Subscription',
    plans: 'Plans',
    billing: 'Billing',
    mcpServers: 'MCP Servers',
    portals: 'Portals',
    statistics: 'Statistics',
    appMarketplace: 'App marketplace',
    customIntegrations: 'Custom integrations',
  },
};
